import  { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import './Sip.css';
import Gratuity from './Gratuity';
import BmiCalculator from './BmiCalculator';
import LoanCalculator from './loanCalculator';
import HomeTaboola from '../HomeTaboola';

const SipCalculator = () => {
  const [activeTab, setActiveTab] = useState('sip'); // 's  ip', 'gratuity' or 'bmi'

  const headerInfo = useMemo(() => {
    switch (activeTab) {
      case 'sip':
        return { title: 'વ્યાજ ગણતરી', tag: 'Interest Calculation' };
      case 'gratuity':
        return { title: 'ગ્રેચ્યુઇટી ગણતરી', tag: 'Gratuity Calculation' };
      case 'bmi':
        return { title: 'સ્વાસ્થ્ય ગણતરી', tag: 'Health Calculation' };
      case 'loan':
        return { title: 'લોન ગણતરી', tag: 'Loan Calculation' };
      default:
        return { title: 'વ્યાજ ગણતરી', tag: 'Interest Calculation' };
    }
  }, [activeTab]);

  const [mode, setMode] = useState('sip'); // 'sip' or 'lumpsum'
  const [amount, setAmount] = useState(mode === 'sip' ? 5000 : 100000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  // Sync amount when mode changes
  useEffect(() => {
    if (mode === 'sip' && amount > 100000) {
      setAmount(100000);
    } else if (mode === 'lumpsum' && amount < 1000) {
      setAmount(1000);
    }
  }, [mode]);

  const results = useMemo(() => {
    const P = parseFloat(amount) || 0;
    const i = (parseFloat(rate) || 0) / 100;
    const n = (parseFloat(years) || 0) * 12;

    let investedAmount, totalValue, estimatedReturns;

    if (mode === 'sip') {
      investedAmount = P * n;
      // CAGR-based SIP formula: r = (1+i)^(1/12) - 1
      const r = Math.pow(1 + i, 1 / 12) - 1;
      totalValue = r === 0 ? investedAmount : P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    } else {
      investedAmount = P;
      totalValue = P * Math.pow(1 + i, parseFloat(years) || 0);
    }

    estimatedReturns = Math.max(0, totalValue - investedAmount);

    return {
      investedAmount: Math.round(investedAmount),
      estimatedReturns: Math.round(estimatedReturns),
      totalValue: Math.round(totalValue)
    };
  }, [mode, amount, rate, years]);

  const chartData = [
    { name: 'Invested', value: results.investedAmount, color: '#dbeafe' },
    { name: 'Returns', value: results.estimatedReturns, color: '#ae0a0b' }
  ];

  const formatCurrency = (num) => {
    return '₹ ' + num.toLocaleString('en-IN');
  };

  const handleAmountChange = (val) => {
    const max = mode === 'sip' ? 1000000 : 10000000;
    const value = Math.min(Math.max(0, parseInt(val) || 0), max);
    setAmount(value);
  };

  const getSliderStyle = (val, min, max) => {
    const percentage = ((val - min) / (max - min)) * 100;
    return {
      background: `linear-gradient(to right, #000000 ${percentage}%, #e5e7eb ${percentage}%)`
    };
  };

  const TitleTag = window.location.pathname === "/calculator" ? "h1" : "h2";

  return (
    <>
<div>
      <div className="sip-calculator-container">
        {/* Side-by-Side Minimalist Header */}
        {window.location.pathname === "/appcalculator" ? "" : <div className="sip-main-title-box">
          <div className="sip-minimal-header">
            <TitleTag className="sip-title-minimal">{headerInfo.title}</TitleTag>
            <span className="sip-title-separator">|</span>
            <span className="sip-tag-minimal">{headerInfo.tag}</span>
          </div>
        </div>}

        {/* Top Navigation */}
        <div className="sip-header-tabs">
          <button
            className={`sip-nav-tab ${activeTab === 'sip' ? 'active' : ''}`}
            onClick={() => setActiveTab('sip')}
          >
            SIP Calculator
          </button>
          <button
            className={`sip-nav-tab ${activeTab === 'gratuity' ? 'active' : ''}`}
            onClick={() => setActiveTab('gratuity')}
          >
            Gratuity Calculator
          </button>
          <button
            className={`sip-nav-tab ${activeTab === 'bmi' ? 'active' : ''}`}
            onClick={() => setActiveTab('bmi')}
          >
            BMI Calculator
          </button>
          <button
            className={`sip-nav-tab ${activeTab === 'loan' ? 'active' : ''}`}
            onClick={() => setActiveTab('loan')}
          >
            Loan Calculator
          </button>
        </div>

        {activeTab === 'sip' && (
          <>
            {/* SIP / Lumpsum Toggle */}
            <div className="sip-mode-selector">
              <button
                className={`sip-mode-btn ${mode === 'sip' ? 'active' : ''}`}
                onClick={() => setMode('sip')}
              >
                SIP
              </button>
              <button
                className={`sip-mode-btn ${mode === 'lumpsum' ? 'active' : ''}`}
                onClick={() => setMode('lumpsum')}
              >
                Lumpsum
              </button>
            </div>

            <div className="sip-main-content">
              {/* Left Side: Inputs */}
              <div className="sip-input-section">
                {/* Investment Amount */}
                <div className="sip-input-group">
                  <div className="sip-input-header">
                    <label className="sip-label">
                      {mode === 'sip' ? 'Monthly investment' : 'Total investment'}
                    </label>
                    <div className="sip-value-box">
                      <span className="sip-symbol">₹</span>
                      <input
                        type="number"
                        className="sip-number-input"
                        value={amount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    className="sip-slider"
                    min={mode === 'sip' ? 100 : 500}
                    max={mode === 'sip' ? 500000 : 10000000}
                    step={mode === 'sip' ? 100 : 1000}
                    value={amount}
                    style={getSliderStyle(amount, mode === 'sip' ? 100 : 500, mode === 'sip' ? 500000 : 10000000)}
                    onChange={(e) => handleAmountChange(e.target.value)}
                  />
                  <div className="sip-range-labels">
                    <span>{mode === 'sip' ? '₹100' : '₹500'}</span>
                    <span>{mode === 'sip' ? '₹5 Lakh' : '₹1 Cr'}</span>
                  </div>
                </div>

                {/* Rate of Return */}
                <div className="sip-input-group">
                  <div className="sip-input-header">
                    <label className="sip-label">Expected return rate (p.a)</label>
                    <div className="sip-value-box">
                      <input
                        type="number"
                        className="sip-number-input"
                        value={rate}
                        step="0.1"
                        onChange={(e) => setRate(Math.min(30, Math.max(1, parseFloat(e.target.value) || 1)))}
                      />
                      <span className="sip-symbol" style={{ marginLeft: '4px' }}>%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    className="sip-slider"
                    min="1"
                    max="30"
                    step="0.1"
                    value={rate}
                    style={getSliderStyle(rate, 1, 30)}
                    onChange={(e) => setRate(parseFloat(e.target.value))}
                  />
                  <div className="sip-range-labels">
                    <span>1</span>
                    <span>30</span>
                  </div>
                </div>

                {/* Time Period */}
                <div className="sip-input-group">
                  <div className="sip-input-header">
                    <label className="sip-label">Time period</label>
                    <div className="sip-value-box">
                      <input
                        type="number"
                        className="sip-number-input"
                        value={years}
                        onChange={(e) => setYears(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                      />
                      <span className="sip-symbol" style={{ marginLeft: '4px' }}>Yr</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    className="sip-slider"
                    min="1"
                    max="30"
                    step="1"
                    value={years}
                    style={getSliderStyle(years, 1, 30)}
                    onChange={(e) => setYears(parseInt(e.target.value))}
                  />
                  <div className="sip-range-labels">
                    <span>1 Yr</span>
                    <span>30 Yr</span>
                  </div>
                </div>
              </div>

              {/* Right Side: Results & Chart */}
              <div className="sip-result-section">
                <div className="sip-results-wrapper">
                  <div className="sip-stats-container">
                    <div className="sip-stat-item">
                      <span className="sip-stat-label">Invested amount</span>
                      <span className="sip-stat-value">{formatCurrency(results.investedAmount)}</span>
                    </div>
                    <div className="sip-stat-item">
                      <span className="sip-stat-label">Est. returns</span>
                      <span className="sip-stat-value">{formatCurrency(results.estimatedReturns)}</span>
                    </div>
                    <div className="sip-stat-item">
                      <span className="sip-stat-label">Total value</span>
                      <span className="sip-stat-value">{formatCurrency(results.totalValue)}</span>
                    </div>
                  </div>

                  <div className="sip-visual-container">
                    <div className="sip-chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={0}
                            dataKey="value"
                            stroke="none"
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          />
                          <text
                            x="50%"
                            y="50%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="sip-chart-center-text"
                            style={{ fontSize: '12px', fontWeight: '800', fill: '#020617' }}
                          >
                            {formatCurrency(results.totalValue)}
                          </text>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="sip-chart-legend">
                      <div className="sip-legend-item">
                        <span className="sip-dot sip-dot-invested"></span>
                        <span>Invested amount</span>
                      </div>
                      <div className="sip-legend-item">
                        <span className="sip-dot sip-dot-returns"></span>
                        <span>Est. returns</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {activeTab === 'gratuity' && <Gratuity />}
        {activeTab === 'bmi' && <BmiCalculator />}
        {activeTab === 'loan' && <LoanCalculator />}
      </div>
      <div>

{/* here i want to integrate ads */}

      </div>
      </div>
      {window.location.pathname === "/calculator" && <HomeTaboola key={Math.random} />}
    </>
  );
};

export default SipCalculator;